import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';

const API = process.env.REACT_APP_BACKEND_URL;
const baseUrl = window.location.origin;

/* ═══════════════════════════════════════════════
   LOGIN GATE
   ═══════════════════════════════════════════════ */
const InvestorLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore');
      sessionStorage.setItem('investor_token', data.token);
      sessionStorage.setItem('investor_name', data.name);
      sessionStorage.setItem('investor_company', data.company || '');
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#00FF41]/30 bg-[#00FF41]/5 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00FF41" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <p className="text-[#00FF41] font-mono text-sm mb-1">
            &lt;/AI_covo.tech&gt;<sup className="text-[8px]">&reg;</sup>
          </p>
          <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.3em]">
            AREA RISERVATA INVESTITORI
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-zinc-500 text-[10px] font-mono uppercase mb-2">Email</label>
            <input
              data-testid="investor-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-zinc-800 text-white text-sm font-mono px-4 py-3 focus:border-[#00FF41]/50 focus:outline-none transition-colors"
              placeholder="email@esempio.com"
              required
            />
          </div>
          <div>
            <label className="block text-zinc-500 text-[10px] font-mono uppercase mb-2">Password</label>
            <input
              data-testid="investor-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-zinc-800 text-white text-sm font-mono px-4 py-3 focus:border-[#00FF41]/50 focus:outline-none transition-colors"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div data-testid="investor-login-error" className="p-3 border border-red-500/30 bg-red-500/5">
              <p className="text-red-400 text-xs font-mono">{error}</p>
            </div>
          )}

          <button
            data-testid="investor-login-btn"
            type="submit"
            disabled={loading}
            className="w-full font-mono text-xs uppercase tracking-wider text-black bg-[#00FF41] py-3 hover:bg-[#33FF66] transition-colors disabled:opacity-50"
          >
            {loading ? 'ACCESSO IN CORSO...' : 'ACCEDI'}
          </button>
        </form>

        <p className="text-zinc-700 text-[10px] font-mono text-center mt-8">
          Accesso riservato su invito. Contatta il team per ottenere le credenziali.
        </p>
      </div>
    </main>
  );
};

/* ═══════════════════════════════════════════════
   SECTIONS DATA
   ═══════════════════════════════════════════════ */
const sections = [
  {
    title: 'Strategia & Investimento',
    color: '#FFD700',
    links: [
      { path: '/strategia', label: 'Strategia Finanziaria & Legale', desc: 'Forma giuridica, piano fondi 3 pilastri, budget 88.750, email ChiantiBanca, prossimi passi' },
      { path: '/chiantibanca', label: 'Proposta ChiantiBanca', desc: 'Pitch persuasivo: 30.000/anno x 10 anni, ROI sociale, base legale Art. 37 TUB' },
      { path: '/crowdfunding', label: 'Crowdfunding Territoriale', desc: 'Piano B: 5 comuni della Valdelsa, tagli 1-100 EUR, agevolazioni fiscali' },
      { path: '/angels', label: 'Manifesto Business Angel', desc: 'Email pronta per BA e incubatori italiani, directory 10 reti, campi personalizzabili' },
    ],
  },
  {
    title: 'Il Progetto — Gia Operativo',
    color: '#00FF41',
    links: [
      { path: '/hub', label: 'Social Hub', desc: 'La piattaforma operativa — tutto il progetto in un colpo d\'occhio' },
      { path: '/formazione', label: 'Formazione Digitale', desc: 'Corsi AI, coaching, certificazioni Google — dalla 1a elementare al Master' },
      { path: '/mentor', label: 'Mentor AI', desc: 'Sportello psicologico + AI chat — supporto integrato' },
      { path: '/archivio', label: 'Archivio Digitale', desc: 'Gamification per scuole e associazioni — drag-and-drop, squadre, punteggi' },
      { path: '/generazioni', label: 'Generazioni', desc: '8 video reels — dalla Greatest Generation alla Gen Beta' },
      { path: '/podcast', label: 'Podcast', desc: 'Piano editoriale 10 puntate — la storia, i visionari, la missione' },
    ],
  },
  {
    title: 'Istituzionale',
    color: '#FF6B6B',
    links: [
      { path: '/pitch', label: 'Pitch TEDx', desc: 'Presentazione completa del progetto' },
      { path: '/email', label: 'Email per Enti', desc: 'Template pronti per Comune, ANCI, istituzioni' },
      { path: '/brand', label: 'Brand Identity', desc: 'Logo, colori, tipografia, download kit' },
      { path: '/about', label: 'Chi Siamo', desc: 'La storia, il team, la visione' },
    ],
  },
  {
    title: 'Supporto & Donazioni',
    color: '#00BFFF',
    links: [
      { path: '/dona', label: 'Dona / Iscriviti', desc: 'Sistema cinture 1-999 EUR + tessera 19 EUR' },
      { path: '/tshirt', label: 'Merchandising', desc: 'T-shirt, gadget, portachiavi con brand' },
      { path: '/sede', label: 'La Sede', desc: 'Spazi, orari, doposcuola, stage retribuiti' },
      { path: '/contatti', label: 'Contatti', desc: 'WhatsApp, email, form diretto' },
    ],
  },
  {
    title: 'Admin & Interno',
    color: '#A855F7',
    links: [
      { path: '/social', label: 'Piano Editoriale Social', desc: '54 post pronti con immagini AI — 9 macro-argomenti' },
      { path: '/links', label: 'Roadmap Links', desc: 'Tutti i collegamenti pubblici del progetto' },
      { path: '/recap', label: 'Recap', desc: 'Riepilogo completo del progetto' },
    ],
  },
];

/* ═══════════════════════════════════════════════
   MAIN UTILITY PAGE (post-login)
   ═══════════════════════════════════════════════ */
const UtilityDashboard = ({ investorName }) => {
  const [visibleSections, setVisibleSections] = useState(0);
  const [activeQR, setActiveQR] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleSections(prev => {
        if (prev >= 30) { clearInterval(timer); return prev; }
        return prev + 1;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  const fadeIn = (index) => ({
    opacity: visibleSections >= index ? 1 : 0,
    transform: visibleSections >= index ? 'translateY(0)' : 'translateY(20px)',
    transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
  });

  const toggleQR = (path) => setActiveQR(prev => prev === path ? null : path);

  const handleLogout = () => {
    sessionStorage.removeItem('investor_token');
    sessionStorage.removeItem('investor_name');
    sessionStorage.removeItem('investor_company');
    window.location.reload();
  };

  return (
    <main data-testid="utility-page" className="min-h-screen pt-20 pb-16 bg-black text-white">
      <div className="max-w-3xl mx-auto px-6">

        {/* Header */}
        <div style={fadeIn(0)} className="text-center mb-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-600 mb-4">
            AREA RISERVATA INVESTITORI
          </p>
          <div className="w-16 h-[1px] bg-[#00FF41]/30 mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3">
            &lt;/utility&gt;
          </h1>
          <p className="text-[#00FF41] font-mono text-sm mb-2">
            Benvenuto, <span className="text-white">{investorName}</span>
          </p>
          <button onClick={handleLogout} className="text-zinc-600 font-mono text-[10px] hover:text-zinc-400 transition-colors mt-2">
            [LOGOUT]
          </button>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* SEZIONE HERO — PERCHE DOVREBBE INTERESSARMI */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={fadeIn(1)} className="mb-14 bg-[#0A0A0A] border border-[#FFD700]/30 p-6 sm:p-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-6 text-center">
            PERCHE DOVREBBE INTERESSARTI
          </p>

          <p className="text-zinc-300 text-sm leading-relaxed mb-6 text-center max-w-xl mx-auto">
            Stai guardando il primo <span className="text-white font-bold">Hub Digitale Sociale</span> costruito 
            in Italia per i territori intermedi. Ecco perché e un investimento, non una donazione.
          </p>

          {/* I 4 ritorni */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="border border-[#FFD700]/20 p-5">
              <p className="text-[#FFD700] font-bold text-sm mb-2">1. Ritorno fiscale immediato</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                <span className="text-white font-bold">Detrazione IRPEF 30%</span> (persone fisiche, max 1M/anno) 
                oppure <span className="text-white font-bold">deduzione IRES 30%</span> (societa, max 1,8M) per investimenti 
                in Impresa Sociale di almeno 5 anni. Le erogazioni liberali sono deducibili fino al 
                <span className="text-white font-bold"> 10% del reddito</span> complessivo.
              </p>
              <p className="text-[#FFD700] text-[10px] font-mono mt-2">D.Lgs. 112/2017 + Art. 83 D.Lgs. 117/2017</p>
            </div>
            <div className="border border-[#00FF41]/20 p-5">
              <p className="text-[#00FF41] font-bold text-sm mb-2">2. Ritorno reputazionale</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Il tuo nome/brand associato a un progetto di 
                <span className="text-white font-bold"> rigenerazione sociale e digitale</span> in uno dei territori 
                più iconici d'Italia. Visibilita su piattaforma, eventi, media locali, T-shirt, certificati, 
                comunicati stampa congiunti. <span className="text-white font-bold">Case history</span> per il tuo bilancio sociale.
              </p>
              <p className="text-[#00FF41] text-[10px] font-mono mt-2">Naming partner + co-branding permanente</p>
            </div>
            <div className="border border-purple-500/20 p-5">
              <p className="text-purple-400 font-bold text-sm mb-2">3. Ritorno di impatto misurabile</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Non promettiamo impatto — lo <span className="text-white font-bold">misuriamo</span>. Report trimestrale con: 
                persone formate, ore di coaching erogate, accessi sportello psicologico, famiglie supportate, 
                NEET riattivati, aziende coinvolte. Metriche certificate, non storytelling.
              </p>
              <p className="text-purple-400 text-[10px] font-mono mt-2">KPI verificabili ogni trimestre</p>
            </div>
            <div className="border border-[#00BFFF]/20 p-5">
              <p className="text-[#00BFFF] font-bold text-sm mb-2">4. Sicurezza giuridica</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                <span className="text-white font-bold">Cooperativa Sociale iscritta al RUNTS</span> — regime fiscale stabile 
                dal 2026 (DL 84/2025), utili non imponibili se reinvestiti, supervisione ministeriale. 
                Non e una startup che può fallire domani: e un ente del Terzo Settore con obblighi di trasparenza, 
                bilancio sociale e rendicontazione pubblica.
              </p>
              <p className="text-[#00BFFF] text-[10px] font-mono mt-2">RUNTS + D.Lgs. 117/2017 (Codice Terzo Settore)</p>
            </div>
          </div>

          {/* I numeri che contano */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="text-center p-3 border border-zinc-800">
              <p className="text-2xl sm:text-3xl font-black text-[#00FF41]">88.750</p>
              <p className="text-zinc-500 text-[10px] font-mono">BUDGET ANNUALE</p>
            </div>
            <div className="text-center p-3 border border-zinc-800">
              <p className="text-2xl sm:text-3xl font-black text-[#FFD700]">73.200</p>
              <p className="text-zinc-500 text-[10px] font-mono">BACINO 5 COMUNI</p>
            </div>
            <div className="text-center p-3 border border-zinc-800">
              <p className="text-2xl sm:text-3xl font-black text-white">2.500</p>
              <p className="text-zinc-500 text-[10px] font-mono">SOCI OBIETTIVO</p>
            </div>
          </div>

          {/* La domanda risolta */}
          <div className="p-4 bg-[#FFD700]/5 border border-[#FFD700]/20 text-center">
            <p className="text-[#FFD700] text-sm font-bold mb-2">
              "Che sicurezze ho?"
            </p>
            <p className="text-zinc-400 text-xs leading-relaxed max-w-lg mx-auto">
              Ente vigilato, bilancio pubblico, rendicontazione obbligatoria, 
              agevolazioni fiscali garantite per legge, impatto misurabile ogni 90 giorni. 
              Non chiediamo fiducia cieca — chiediamo 30 minuti per mostrarti i numeri.
            </p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* COSA RISCHIA CHI NON INVESTE */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={fadeIn(3)} className="mb-14">
          <div className="border-l-4 border-red-500/50 pl-5 py-2">
            <p className="text-red-400 font-mono text-[10px] uppercase tracking-wider mb-2">COSTO DELL'INAZIONE</p>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Ogni anno che passa senza un Hub Digitale Sociale, la Valdelsa perde giovani, 
              competenze e opportunita. I fondi PNRR scadono. I capannoni restano vuoti. 
              I NEET aumentano. Il costo di 
              <span className="text-white font-bold"> non fare niente</span> e molto più alto 
              di <span className="text-[#00FF41] font-bold">88.750 EUR/anno</span>.
            </p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* QR CODE MASTER */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={fadeIn(4)} className="mb-14 bg-[#0A0A0A] border border-[#00FF41]/30 p-6 sm:p-8 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00FF41] mb-4">
            QR CODE — QUESTA PAGINA
          </p>
          <p className="text-zinc-400 text-xs mb-6">
            Condividi questo QR con altri potenziali investitori
          </p>
          <div className="inline-block p-4 bg-white rounded">
            <QRCode value={`${baseUrl}/utility`} size={180} level="H" fgColor="#000000" bgColor="#FFFFFF" />
          </div>
          <p className="text-zinc-500 font-mono text-[10px] mt-4">{baseUrl}/utility</p>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* LINK SECTIONS */}
        {/* ═══════════════════════════════════════════════════════ */}
        {sections.map((section, sIdx) => (
          <div key={sIdx} style={fadeIn(5 + sIdx * 2)} className="mb-12">
            <p className="font-mono text-xs uppercase tracking-[0.2em] mb-5" style={{ color: section.color }}>
              {section.title}
            </p>
            <div className="space-y-2">
              {section.links.map((link, lIdx) => (
                <div key={lIdx} className="border border-zinc-800 hover:border-zinc-600 transition-colors">
                  <div className="flex items-center gap-3 p-4">
                    <button
                      data-testid={`qr-btn-${link.path.replace('/', '')}`}
                      onClick={() => toggleQR(link.path)}
                      className="w-8 h-8 flex items-center justify-center border font-mono text-[10px] font-bold flex-shrink-0 transition-all duration-300"
                      style={{
                        borderColor: activeQR === link.path ? section.color : '#444',
                        color: activeQR === link.path ? '#000' : section.color,
                        backgroundColor: activeQR === link.path ? section.color : 'transparent',
                      }}
                    >
                      QR
                    </button>
                    <Link to={link.path} className="flex-1 min-w-0 group">
                      <p className="text-white font-bold text-sm group-hover:underline transition-colors" style={{ textDecorationColor: section.color }}>
                        {link.label}
                      </p>
                      <p className="text-zinc-500 text-[10px] mt-0.5 leading-relaxed">{link.desc}</p>
                    </Link>
                    <Link to={link.path} className="text-zinc-600 hover:text-white transition-colors flex-shrink-0 font-mono text-xs">
                      &#8250;
                    </Link>
                  </div>
                  {activeQR === link.path && (
                    <div className="px-4 pb-4 text-center border-t border-zinc-800 pt-4" style={{ backgroundColor: `${section.color}05` }}>
                      <div className="inline-block p-3 bg-white rounded">
                        <QRCode value={`${baseUrl}${link.path}`} size={150} level="H" fgColor="#000000" bgColor="#FFFFFF" />
                      </div>
                      <p className="text-zinc-500 font-mono text-[10px] mt-3">{baseUrl}{link.path}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(`${baseUrl}${link.path}`)}
                        className="mt-2 font-mono text-[10px] px-3 py-1 border transition-colors"
                        style={{ borderColor: section.color, color: section.color }}
                      >
                        COPIA LINK
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Nota finale */}
        <div style={fadeIn(17)} className="mb-14 p-4 border border-zinc-800 bg-[#0A0A0A]">
          <p className="text-zinc-500 text-[10px] font-mono uppercase mb-2">NOTA</p>
          <p className="text-zinc-400 text-xs leading-relaxed">
            Questa pagina non e indicizzata e non appare nel menu di navigazione. 
            Accessibile solo via link diretto, QR code o credenziali inviate dal team.
          </p>
        </div>

        {/* Footer */}
        <div style={fadeIn(18)} className="text-center text-zinc-600 text-xs border-t border-zinc-900 pt-8 pb-4">
          <p className="text-zinc-500 font-mono text-[10px] mb-2">
            &lt;/AI_COVO.tech&gt;® — Investor Utility Hub
          </p>
          <p>{new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p className="text-zinc-700 mt-1">Colle Val d'Elsa — Toscana — Italia</p>
        </div>
      </div>
    </main>
  );
};

/* ═══════════════════════════════════════════════
   WRAPPER — LOGIN GATE + DASHBOARD
   ═══════════════════════════════════════════════ */
const UtilityPage = () => {
  const [authed, setAuthed] = useState(false);
  const [investorName, setInvestorName] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('investor_token');
    if (token) {
      fetch(`${API}/api/investor/verify?token=${token}`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => {
          setInvestorName(data.name);
          setAuthed(true);
        })
        .catch(() => {
          sessionStorage.removeItem('investor_token');
        })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-[#00FF41] font-mono text-xs animate-pulse">VERIFICA ACCESSO...</p>
      </main>
    );
  }

  if (!authed) {
    return <InvestorLogin onLogin={(data) => { setInvestorName(data.name); setAuthed(true); }} />;
  }

  return <UtilityDashboard investorName={investorName} />;
};

export default UtilityPage;
